export interface TurnstileWidgetOptions {
  sitekey: string;
  theme?: 'auto' | 'light' | 'dark';
  size?: 'normal' | 'flexible' | 'compact';
  appearance?: 'always' | 'execute' | 'interaction-only';
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileWidgetOptions): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export const TURNSTILE_SCRIPT_TIMEOUT_MS = 10_000;

let pending: Promise<TurnstileApi> | null = null;

function existingScript(): HTMLScriptElement | null {
  return document.querySelector<HTMLScriptElement>('script[data-luacentrix-turnstile="1"]');
}

function injectScript(): Promise<TurnstileApi> {
  return new Promise<TurnstileApi>((resolve, reject) => {
    let settled = false;
    const finish = (outcome: 'ready' | 'failed'): void => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      const api = window.turnstile;
      if (outcome === 'ready' && api !== undefined) {
        resolve(api);
        return;
      }
      reject(new Error('turnstile_unavailable'));
    };
    const timer = window.setTimeout(() => finish('failed'), TURNSTILE_SCRIPT_TIMEOUT_MS);
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.dataset['luacentrixTurnstile'] = '1';
    script.addEventListener('load', () => finish('ready'));
    script.addEventListener('error', () => finish('failed'));
    document.head.append(script);
  });
}

export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile !== undefined) {
    return Promise.resolve(window.turnstile);
  }
  if (pending !== null) {
    return pending;
  }
  const attempt = existingScript() === null ? injectScript() : waitForExistingScript();
  pending = attempt;
  attempt.catch(() => {
    if (pending === attempt) {
      pending = null;
    }
  });
  return attempt;
}

function waitForExistingScript(): Promise<TurnstileApi> {
  return new Promise<TurnstileApi>((resolve, reject) => {
    const started = Date.now();
    const poll = window.setInterval(() => {
      if (window.turnstile !== undefined) {
        window.clearInterval(poll);
        resolve(window.turnstile);
        return;
      }
      if (Date.now() - started >= TURNSTILE_SCRIPT_TIMEOUT_MS) {
        window.clearInterval(poll);
        reject(new Error('turnstile_unavailable'));
      }
    }, 100);
  });
}
